import { DECADES, GENRES, type Decade, type Genre, type Track } from "./domain.ts";

/**
 * How the Catalog travels to the browser.
 *
 * `public/catalog.json` is the harvest's output and the source of truth: one fat
 * object per Track, readable in a diff, carrying fields the game never uses
 * (`isrc`, kept for the future Spotify bridge). It is not what gets delivered.
 *
 * Measured on the 7,993-track Catalog, 68% of that 2.95MB is waste:
 *
 *   - **31%** is the eleven JSON key names, repeated 7,993 times.
 *   - **27%** is artwork URLs, of which only a 32-character hash ever varies —
 *     every one is `…/images/cover/<hash>/250x250-000000-80-0-0.jpg`.
 *   - **10%** is float noise like `0.6971017000000002`, where four decimals is
 *     already finer than any comparison the game makes.
 *
 * So the wire format is columnar: one array per field and no keys at all, with
 * decades and genres as indices, artwork as the bare hash, and scores rounded.
 * Roughly a third of the size, and it decodes in one pass with no parsing beyond
 * `JSON.parse`.
 *
 * Both the packer and the browser import this module, so the two halves of the
 * format cannot drift apart.
 */

/** The invariant half of every Deezer cover URL. */
const ART_PREFIX = "https://cdn-images.dzcdn.net/images/cover/";
const ART_SUFFIX = "/250x250-000000-80-0-0.jpg";

/** Bumped if the column layout changes, so a stale cached file is rejected. */
export const WIRE_VERSION = 1;

export interface WireCatalog {
  wire: number;
  builtAt: string;
  markets: readonly string[];
  /** How many Tracks: every column has exactly this length. */
  n: number;
  id: number[];
  title: string[];
  artist: string[];
  artistId: number[];
  /** Bare artwork hash, or "" when the Track has no cover. */
  art: string[];
  /** Index into DECADES. */
  dec: number[];
  /** Indices into GENRES, one list per Track. */
  gen: number[][];
  rec: number[];
  pct: number[];
  hook: number[];
}

/** The payload that lets Daily start before the song list has arrived. */
export interface WireBoot {
  wire: number;
  builtAt: string;
  markets: readonly string[];
  /** Total Tracks in the full list, for UI that wants to show catalog size. */
  trackCount: number;
  /** Local date key → that day's five Tracks, precomputed at build time. */
  days: Record<string, WireCatalog>;
}

const round4 = (n: number) => Math.round(n * 1e4) / 1e4;

function artHash(url: string | null): string {
  if (!url) return "";
  if (url.startsWith(ART_PREFIX) && url.endsWith(ART_SUFFIX)) {
    return url.slice(ART_PREFIX.length, url.length - ART_SUFFIX.length);
  }
  // An unexpected shape is carried whole rather than mangled — better a few long
  // strings than a broken image.
  return url;
}

function artUrl(hash: string): string | null {
  if (hash === "") return null;
  if (hash.startsWith("http")) return hash;
  return `${ART_PREFIX}${hash}${ART_SUFFIX}`;
}

export function packTracks(
  tracks: Track[],
  meta: { builtAt: string; markets: readonly string[] },
): WireCatalog {
  return {
    wire: WIRE_VERSION,
    builtAt: meta.builtAt,
    markets: meta.markets,
    n: tracks.length,
    id: tracks.map((t) => t.id),
    title: tracks.map((t) => t.title),
    artist: tracks.map((t) => t.artist),
    artistId: tracks.map((t) => t.artistId),
    art: tracks.map((t) => artHash(t.artworkUrl)),
    dec: tracks.map((t) => DECADES.indexOf(t.decade)),
    gen: tracks.map((t) => t.genres.map((g) => GENRES.indexOf(g)).filter((i) => i >= 0)),
    rec: tracks.map((t) => round4(t.recognisability)),
    pct: tracks.map((t) => round4(t.percentile)),
    hook: tracks.map((t) => t.hookStart),
  };
}

/**
 * Rebuilds Tracks from columns.
 *
 * Tolerant of a short or missing column: a truncated file yields fewer Tracks
 * rather than a catalog full of `undefined`, which would fail much later and much
 * more confusingly.
 */
export function unpackTracks(wire: WireCatalog): Track[] {
  const out: Track[] = [];
  const count = Math.min(
    wire.n ?? 0,
    wire.id?.length ?? 0,
    wire.title?.length ?? 0,
    wire.artist?.length ?? 0,
  );

  for (let i = 0; i < count; i++) {
    const decade = DECADES[wire.dec?.[i] ?? -1];
    if (!decade) continue;

    out.push({
      id: wire.id[i]!,
      title: wire.title[i]!,
      artist: wire.artist[i]!,
      artistId: wire.artistId?.[i] ?? 0,
      // Dropped from the wire entirely — nothing at runtime reads it.
      isrc: null,
      artworkUrl: artUrl(wire.art?.[i] ?? ""),
      decade,
      genres: (wire.gen?.[i] ?? [])
        .map((g) => GENRES[g])
        .filter((g): g is Genre => g !== undefined),
      recognisability: wire.rec?.[i] ?? 0,
      percentile: wire.pct?.[i] ?? 0,
      hookStart: wire.hook?.[i] ?? 0,
    });
  }

  return out;
}

/** True if this payload was written by a build that agrees with this reader. */
export function isCurrentWire(payload: { wire?: number } | null | undefined): boolean {
  return payload?.wire === WIRE_VERSION;
}

export type { Decade, Genre };
